import { useState } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { toast } from "sonner";
import { Loader2, Sparkles, ClipboardList } from "lucide-react";
import CreditCostTag from "../../CreditCostTag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PilotPlanTab({ projectId, token, pilotPlan, setPilotPlan }) {
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const aiSuggest = async () => {
    setAiLoading(true);
    try {
      const response = await fetch(`${API}/ai/suggest-pilot-plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_id: projectId }),
      });
      if (!response.ok) throw new Error("AI pilot plan generation failed");
      const data = await response.json();
      if (data.pilot_plan) {
        const pp = data.pilot_plan;
        const flatten = (val) => typeof val === 'string' ? val : typeof val === 'object' && val !== null ? Object.values(val).join('\n') : String(val || '');
        setPilotPlan({
          hypothesis: flatten(pp.hypothesis),
          test_design: flatten(pp.test_design),
          success_metrics: flatten(pp.success_metrics),
          duration: flatten(pp.duration),
          sample_size_target: flatten(pp.sample_size_target),
          rollback_plan: flatten(pp.rollback_plan),
        });
        toast.success("Pilot plan generated");
      }
    } catch (error) { toast.error(error.message); }
    finally { setAiLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/projects/${projectId}/improve/pilot-plan`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(pilotPlan),
      });
      if (!response.ok) throw new Error("Failed to save pilot plan");
      toast.success("Pilot plan saved");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="pilot-plan-form">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-3">
          <ClipboardList className="w-5 h-5 text-primary" />
          <h3 className="font-heading font-semibold">Pilot Plan</h3>
        </div>
        <Button variant="outline" size="sm" onClick={aiSuggest} disabled={aiLoading} data-testid="ai-suggest-pilot-btn">
          {aiLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
          {aiLoading ? "Generating..." : "AI Suggest"}
          <CreditCostTag cost={5} />
        </Button>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Required before gate advancement. Document your experiment design.
      </p>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Hypothesis</Label>
          <Textarea value={pilotPlan.hypothesis} onChange={(e) => setPilotPlan(p => ({ ...p, hypothesis: e.target.value }))} placeholder="State your improvement hypothesis..." rows={2} data-testid="pilot-hypothesis" />
        </div>
        <div className="space-y-2">
          <Label>Test Design</Label>
          <Textarea value={pilotPlan.test_design} onChange={(e) => setPilotPlan(p => ({ ...p, test_design: e.target.value }))} placeholder="Describe the test methodology, scope, and control group..." rows={3} data-testid="pilot-test-design" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Success Metrics</Label>
            <Input value={pilotPlan.success_metrics} onChange={(e) => setPilotPlan(p => ({ ...p, success_metrics: e.target.value }))} placeholder="e.g., Defect rate < 2%" data-testid="pilot-success-metrics" />
          </div>
          <div className="space-y-2">
            <Label>Duration</Label>
            <Input value={pilotPlan.duration} onChange={(e) => setPilotPlan(p => ({ ...p, duration: e.target.value }))} placeholder="e.g., 4 weeks" data-testid="pilot-duration" />
          </div>
          <div className="space-y-2">
            <Label>Sample Size Target</Label>
            <Input value={pilotPlan.sample_size_target} onChange={(e) => setPilotPlan(p => ({ ...p, sample_size_target: e.target.value }))} placeholder="e.g., 100 units" data-testid="pilot-sample-size" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Rollback Plan</Label>
          <Textarea value={pilotPlan.rollback_plan} onChange={(e) => setPilotPlan(p => ({ ...p, rollback_plan: e.target.value }))} placeholder="What happens if the pilot fails? How do you revert?" rows={2} data-testid="pilot-rollback" />
        </div>
      </div>
      <Button onClick={save} disabled={saving} className="mt-4" data-testid="save-pilot-plan-btn">
        {saving ? "Saving..." : "Save Pilot Plan"}
      </Button>
    </div>
  );
}
